import React, { useState } from "react";
import {
  createProvider,
  updateProvider,
  type Provider,
  type CreateProviderResult,
} from "../api/client";

const VALIDITY_OPTIONS: { label: string; days: number | null }[] = [
  { label: "30 giorni", days: 30 },
  { label: "90 giorni", days: 90 },
  { label: "1 anno", days: 365 },
  { label: "2 anni", days: 730 },
  { label: "Nessuna scadenza", days: null },
];

const RECOGNITION_OPTIONS = [
  { value: "FEA_SELFID", label: "SELFID", desc: "Identità digitale con selfie" },
  { value: "FEA_SPID", label: "SPID", desc: "Sistema Pubblico di Identità Digitale" },
  { value: "FEA_CIE", label: "CIE", desc: "Carta d'Identità Elettronica" },
];

const WIZARD_STEPS = [
  { label: "Configurazione" },
  { label: "Riepilogo" },
] as const;

interface Props {
  provider?: Provider;
  onDone: (created?: CreateProviderResult) => void;
  onCancel: () => void;
}

function WizardStepper({ current }: { current: number }) {
  return (
    <div className="wizard-stepper">
      {WIZARD_STEPS.map((s, i) => {
        const cls = i < current ? "done" : i === current ? "active" : "";
        return (
          <div key={i} className={`wizard-step ${cls}`}>
            <div className="wizard-step-circle">
              {i < current ? (
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check</span>
              ) : (
                i + 1
              )}
            </div>
            <span className="wizard-step-label">{s.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function ApiKeyBox({ apiKey }: { apiKey: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  return (
    <div style={{ display: "flex", gap: "var(--space-2)" }}>
      <input
        className="form-input"
        readOnly
        value={apiKey}
        style={{ fontFamily: "monospace", fontSize: "var(--font-size-sm)" }}
      />
      <button className="btn btn-outline" onClick={copy} style={{ flexShrink: 0 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
          {copied ? "check" : "content_copy"}
        </span>
        {copied ? "Copiato!" : "Copia"}
      </button>
    </div>
  );
}

export function ProviderForm({ provider, onDone, onCancel }: Props) {
  const isEdit = !!provider;

  const [name, setName] = useState(provider?.name ?? "");
  const [description, setDescription] = useState(provider?.description ?? "");
  const [products, setProducts] = useState<string[]>(
    provider?.allowed_products ?? ["top"],
  );
  const [recognitionTypes, setRecognitionTypes] = useState<string[]>(
    provider?.allowed_recognition_types ?? ["FEA_SELFID"],
  );
  const [webhookUrl, setWebhookUrl] = useState(provider?.webhook_url ?? "");
  const [validityDays, setValidityDays] = useState<number | null>(365);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [createdResult, setCreatedResult] = useState<CreateProviderResult | null>(null);

  const toggleProduct = (p: string) =>
    setProducts((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const toggleRecognitionType = (t: string) =>
    setRecognitionTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const validate = (): string => {
    if (!name.trim()) return "Il nome è obbligatorio.";
    if (products.length === 0) return "Seleziona almeno un prodotto.";
    if (products.includes("top") && recognitionTypes.length === 0)
      return "Seleziona almeno un tipo di riconoscimento per TOP.";
    if (webhookUrl && !webhookUrl.startsWith("https://"))
      return "Il Webhook URL deve iniziare con https://.";
    return "";
  };

  const handleNext = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setStep(1);
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setSubmitting(true);
    try {
      if (isEdit) {
        await updateProvider(provider!.id, {
          name: name.trim(),
          ...(description.trim() && { description: description.trim() }),
          allowed_products: products,
          allowed_recognition_types: recognitionTypes,
          ...(webhookUrl.trim() && { webhook_url: webhookUrl.trim() }),
        });
        onDone();
      } else {
        const result = await createProvider({
          name: name.trim(),
          ...(description.trim() && { description: description.trim() }),
          allowed_products: products,
          allowed_recognition_types: recognitionTypes,
          ...(webhookUrl.trim() && { webhook_url: webhookUrl.trim() }),
          ...(validityDays !== null && { expires_in_days: validityDays }),
        });
        setCreatedResult(result);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Post-create: show the new API key ── */
  if (createdResult) {
    return (
      <div>
        <h1 className="page-title">Provider creato</h1>
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-5)" }}>
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 36, color: "var(--indaco-success)" }}
            >
              check_circle
            </span>
            <h2 className="section-title" style={{ margin: 0 }}>
              {createdResult.name} creato con successo
            </h2>
          </div>

          <div
            style={{
              background: "var(--indaco-warning-light)",
              border: "1px solid var(--indaco-warning)",
              borderRadius: "var(--border-radius-md)",
              padding: "var(--space-4)",
              marginBottom: "var(--space-4)",
              display: "flex",
              gap: "var(--space-3)",
              alignItems: "flex-start",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ color: "var(--indaco-warning)", flexShrink: 0 }}
            >
              warning
            </span>
            <div>
              <strong style={{ display: "block", marginBottom: 4 }}>
                Salva la chiave API ora
              </strong>
              <p
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--indaco-gray-700)",
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Questa chiave non sarà più visibile dopo aver lasciato questa
                pagina. Conservala in un luogo sicuro.
              </p>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Chiave API</label>
            <ApiKeyBox apiKey={createdResult.api_key} />
          </div>

          <button className="btn btn-primary" onClick={() => onDone(createdResult)}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
              arrow_back
            </span>
            Torna ai provider
          </button>
        </div>
      </div>
    );
  }

  /* ── Shared field renderers ── */
  const renderFields = () => (
    <div className="card">
      <h2 className="section-title">
        {isEdit ? `Modifica ${provider!.name}` : "Configurazione provider"}
      </h2>

      <div className="form-group">
        <label className="form-label">Nome *</label>
        <input
          className="form-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Es. Acquirente Unico"
          autoFocus
        />
      </div>

      <div className="form-group">
        <label className="form-label">Descrizione</label>
        <textarea
          className="form-textarea"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descrizione opzionale del provider..."
          style={{ resize: "vertical" }}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Prodotti abilitati</label>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={products.includes("top")}
              onChange={() => toggleProduct("top")}
            />
            <span style={{ fontWeight: "var(--font-weight-semibold)" }}>TOP</span>
            <span
              style={{
                fontSize: "var(--font-size-sm)",
                color: "var(--indaco-gray-600)",
              }}
            >
              — Firma InfoCert TOP
            </span>
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              cursor: "not-allowed",
              opacity: 0.5,
            }}
            title="Coming soon"
          >
            <input type="checkbox" disabled checked={false} onChange={() => {}} />
            <span style={{ fontWeight: "var(--font-weight-semibold)" }}>FEA</span>
            <span
              style={{
                fontSize: "var(--font-size-sm)",
                color: "var(--indaco-gray-600)",
              }}
            >
              — Firma Elettronica Avanzata
            </span>
            <span className="badge badge-neutral" style={{ marginLeft: 4 }}>
              Coming soon
            </span>
          </label>
        </div>
      </div>

      {products.includes("top") && (
        <div className="form-group">
          <label className="form-label">Tipi di riconoscimento (TOP)</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {RECOGNITION_OPTIONS.map((rt) => (
              <label
                key={rt.value}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-2)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={recognitionTypes.includes(rt.value)}
                  onChange={() => toggleRecognitionType(rt.value)}
                />
                <span style={{ fontWeight: "var(--font-weight-semibold)" }}>
                  {rt.label}
                </span>
                <span
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--indaco-gray-600)",
                  }}
                >
                  — {rt.desc}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Webhook URL (opzionale)</label>
        <input
          className="form-input"
          type="url"
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          placeholder="https://..."
        />
        {webhookUrl && !webhookUrl.startsWith("https://") && (
          <span
            style={{
              fontSize: "var(--font-size-xs)",
              color: "var(--indaco-danger)",
              display: "block",
              marginTop: 4,
            }}
          >
            Il Webhook URL deve iniziare con https://
          </span>
        )}
      </div>

      {!isEdit && (
        <div className="form-group">
          <label className="form-label">Validità chiave API</label>
          <select
            className="form-select"
            value={validityDays === null ? "null" : String(validityDays)}
            onChange={(e) =>
              setValidityDays(e.target.value === "null" ? null : Number(e.target.value))
            }
          >
            {VALIDITY_OPTIONS.map((opt) => (
              <option key={String(opt.days)} value={String(opt.days)}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  const renderReview = () => {
    const validityLabel =
      VALIDITY_OPTIONS.find((o) => o.days === validityDays)?.label ?? "Nessuna scadenza";
    return (
      <div className="card">
        <h2 className="section-title">Riepilogo</h2>
        <p
          style={{
            fontSize: "var(--font-size-sm)",
            color: "var(--indaco-gray-600)",
            marginBottom: "var(--space-4)",
          }}
        >
          Verifica i dati prima di creare il provider.
        </p>
        <div className="detail-grid">
          <div>
            <span className="detail-label">Nome</span>
            <span className="detail-value">{name}</span>
          </div>
          {description.trim() && (
            <div>
              <span className="detail-label">Descrizione</span>
              <span className="detail-value">{description}</span>
            </div>
          )}
          <div>
            <span className="detail-label">Prodotti</span>
            <span className="detail-value">
              {products.map((p) => p.toUpperCase()).join(", ") || "—"}
            </span>
          </div>
          <div>
            <span className="detail-label">Tipi di riconoscimento</span>
            <span className="detail-value">
              {recognitionTypes.join(", ") || "—"}
            </span>
          </div>
          {webhookUrl.trim() && (
            <div>
              <span className="detail-label">Webhook URL</span>
              <span
                className="detail-value"
                style={{
                  fontFamily: "monospace",
                  fontSize: "var(--font-size-xs)",
                  wordBreak: "break-all",
                }}
              >
                {webhookUrl}
              </span>
            </div>
          )}
          <div>
            <span className="detail-label">Validità</span>
            <span className="detail-value">{validityLabel}</span>
          </div>
        </div>
      </div>
    );
  };

  /* ── Edit mode: simple form ── */
  if (isEdit) {
    return (
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)",
            marginBottom: "var(--space-4)",
          }}
        >
          <button className="btn btn-ghost" onClick={onCancel}>
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>
              Modifica provider
            </h1>
          </div>
        </div>

        {renderFields()}

        {error && (
          <div className="toast toast-error" style={{ marginTop: "var(--space-3)" }}>
            <span className="material-symbols-outlined">error</span>
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "var(--space-2)",
            marginTop: "var(--space-4)",
          }}
        >
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner" /> Salvataggio…
              </>
            ) : (
              <>
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 18 }}
                >
                  save
                </span>
                Salva modifiche
              </>
            )}
          </button>
          <button
            className="btn btn-outline"
            onClick={onCancel}
            disabled={submitting}
          >
            Annulla
          </button>
        </div>
      </div>
    );
  }

  /* ── Create mode: 2-step wizard ── */
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-3)",
          marginBottom: "var(--space-4)",
        }}
      >
        <button className="btn btn-ghost" onClick={onCancel}>
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className="page-title" style={{ margin: 0 }}>
          Nuovo provider
        </h1>
      </div>

      <WizardStepper current={step} />

      {step === 0 && renderFields()}
      {step === 1 && renderReview()}

      {error && (
        <div className="toast toast-error" style={{ marginTop: "var(--space-3)" }}>
          <span className="material-symbols-outlined">error</span>
          {error}
        </div>
      )}

      <div className="wizard-nav">
        <div>
          {step === 0 ? (
            <button className="btn btn-outline btn-lg" onClick={onCancel}>
              Annulla
            </button>
          ) : (
            <button
              className="btn btn-outline btn-lg"
              onClick={() => { setError(""); setStep(0); }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 18 }}
              >
                arrow_back
              </span>
              Indietro
            </button>
          )}
        </div>
        <div>
          {step === 0 ? (
            <button className="btn btn-primary btn-lg" onClick={handleNext}>
              Avanti
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 18 }}
              >
                arrow_forward
              </span>
            </button>
          ) : (
            <button
              className="btn btn-success btn-lg"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner" /> Creazione…
                </>
              ) : (
                <>
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 18 }}
                  >
                    add_circle
                  </span>
                  Crea provider
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
